'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Yogurt: How Cups & Lids Contaminate Dairy"
      author="MicroPlastics Editorial Team"
      date="April 29, 2026"
      readTime="7 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in yogurt cups and lids"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Most yogurt cups are made from <strong>polystyrene (#6)</strong> or
        <strong> polypropylene (#5)</strong>. Both leach microplastics into the
        yogurt, accelerated by the product&apos;s acidity (Greek yogurt
        especially), fat content, and the long storage time between production and
        consumption. The peel-back lid is usually <strong>aluminium foil bonded
        with a PE plastic layer</strong> that contacts the yogurt directly. Glass-jarred
        yogurt (Stonyfield, Maple Hill, some European brands) is the only fully
        plastic-free common option.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Polystyrene (#6) cups leach styrene and shed microplastics. IARC classifies styrene as a possible human carcinogen.</>,
          <>Acidic Greek yogurt (pH ~4.0–4.5) accelerates leaching compared to regular yogurt (pH ~4.4–4.7).</>,
          <>The foil-and-plastic peel lid contains a thin polyethylene layer that contacts the yogurt for weeks during storage.</>,
          <>Higher-fat yogurts pick up more lipophilic plasticisers than non-fat varieties.</>,
          <>Glass-jar yogurt brands and large-format glass-pot yogurts eliminate the bulk of cup-related exposure.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why yogurt cups leach
      </h2>
      <p>
        Three factors combine to make yogurt a higher-than-expected microplastic
        source despite its small per-serving volume:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Acidity.</strong> Yogurt&apos;s lactic acid (pH 4.0–4.7) is acidic
          enough to accelerate plastic surface degradation, especially against
          polystyrene #6 cups.
        </li>
        <li>
          <strong>Storage time.</strong> Most yogurt has a 30–60 day shelf life
          between production and consumption, sitting in direct cup contact the
          entire time.
        </li>
        <li>
          <strong>Fat content.</strong> Plasticisers (phthalates, bisphenols) are
          lipophilic, they preferentially leach into fatty products. Full-fat and
          whole-milk yogurts absorb more chemical leachate than non-fat varieties.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cup material decoded
      </h2>
      <p>
        Check the recycling number on the bottom of the cup:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>#6 PS (polystyrene)</strong>, most common in single-serve cups
          and yogurt drinks. Worst from a microplastic and chemical standpoint.
          IARC classifies styrene as a possible human carcinogen.
        </li>
        <li>
          <strong>#5 PP (polypropylene)</strong>, more common in larger tubs.
          More chemically stable than PS but still sheds particles.
        </li>
        <li>
          <strong>#1 PET</strong>, occasionally used for yogurt drinks; same
          concerns as bottled water (see our{' '}
          <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            bottled water guide
          </Link>).
        </li>
        <li>
          <strong>Glass jar</strong>, used by some European brands and a few US
          premium brands. Inert; zero plastic contact with the yogurt.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Yogurt types compared
      </h2>

      <BlogComparisonTable
        caption="Yogurt packaging types by microplastic exposure"
        columns={[
          { key: 'type', label: 'Yogurt format' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'why', label: 'Why' },
        ]}
        rows={[
          { type: 'Glass-jar yogurt (Stonyfield, Maple Hill, French/European)', risk: 'Lowest', why: 'Glass is inert; only lid is plastic-foil' },
          { type: 'Large PP (#5) tub of plain whole-milk', risk: 'Lower', why: 'PP more stable than PS; less surface-area-per-gram than single-serve' },
          { type: 'Single-serve PS (#6) Greek yogurt', risk: 'Higher', why: 'High acidity + high surface-area-to-volume + #6 polystyrene' },
          { type: 'Yogurt drinks in PET bottles', risk: 'Higher', why: 'Liquid contact + same PET concerns as bottled water' },
          { type: 'Homemade yogurt in glass jar', risk: 'Lowest', why: 'Full ingredient and container control' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Buy glass-jar yogurt</strong> when available, brands include
          Stonyfield Organic Pro Force (some sizes), Maple Hill 100% Grass-Fed
          (some sizes), St. Benoît, La Fermière, and European brands like Yeo
          Valley (UK) and Brunet (France).
        </li>
        <li>
          <strong>Choose large PP tubs over single-serve PS cups.</strong> Better
          surface-area ratio, slightly better polymer.
        </li>
        <li>
          <strong>Transfer to glass before refrigerating.</strong> If you buy a
          plastic-cup product, decanting reduces continued leaching during
          remaining storage.
        </li>
        <li>
          <strong>Don&apos;t microwave yogurt in its cup.</strong> Heat dramatically
          accelerates particle and chemical release.
        </li>
        <li>
          <strong>Make yogurt at home</strong> in a glass jar, needs only milk and
          a starter culture, made in an oven, dehydrator, or Instant Pot.
        </li>
      </ol>

      <p>
        For broader dairy exposure, see{' '}
        <Link href="/blog/microplastics-in-milk-and-dairy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in milk and dairy products
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-plastic-containers" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in plastic containers
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Are yogurt cups bad for microplastics?',
            answer:
              'Yes. Most yogurt cups are made from polystyrene (#6) or polypropylene (#5). Both leach microplastics into the yogurt, accelerated by acidity, fat content, and storage time. Polystyrene also leaches styrene, classified by IARC as possibly carcinogenic.',
          },
          {
            question: 'Is glass-jar yogurt safer than plastic-cup yogurt?',
            answer:
              'Yes. Glass is chemically inert and does not leach microplastics or plasticisers. Only the foil-and-plastic lid touches the yogurt, and only briefly during opening. Brands selling glass yogurt include Stonyfield, Maple Hill, St. Benoît, La Fermière, and many European brands.',
          },
          {
            question: 'Does Greek yogurt have more microplastics than regular?',
            answer:
              'Likely yes per gram. Greek yogurt has slightly lower pH (more acidic) than regular, which accelerates leaching from polystyrene and polypropylene cups. Greek is also typically sold in smaller single-serve PS cups with higher surface-area-to-volume than larger tubs.',
          },
          {
            question: 'What number plastic is yogurt cups?',
            answer:
              'Single-serve yogurt cups are typically #6 polystyrene (PS), which is the worst common food plastic for both microplastics and chemical leaching. Larger tubs are more often #5 polypropylene (PP), which is more stable but still sheds particles.',
          },
          {
            question: 'Can I microwave yogurt in its cup?',
            answer:
              'No. Microwaving plastic yogurt cups dramatically accelerates microplastic and chemical release. A single microwave cycle can release millions of particles. Always transfer to glass or ceramic if you need to warm yogurt.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Da Costa Filho PA, Andrey D, Eriksen B, et al.',
            year: 2021,
            title: 'Detection and characterization of small-sized microplastics (≥ 5 μm) in milk products',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/s41598-021-04489-w',
          },
          {
            authors: 'Diaz-Basantes MF, Conesa JA, Fullana A',
            year: 2020,
            title: 'Microplastics in honey, beer, milk and refreshments in Ecuador',
            publisher: 'Sustainability',
            url: 'https://www.mdpi.com/2071-1050/12/14/5514',
          },
          {
            authors: 'IARC Working Group',
            year: 2019,
            title: 'Styrene, Styrene-7,8-oxide, and Quinoline (IARC Monograph 121)',
            publisher: 'International Agency for Research on Cancer',
            url: 'https://publications.iarc.fr/581',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Re-evaluation of bisphenol A (BPA)',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/6857',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
